// src/App.jsx
import './i18n';
import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./firebase";
import Login from "./Login";
import Register from "./Register";
import Dashboard from "./Dashboard";
import SplashScreen from "./SplashScreen";
import Home from "./Home";
import Reports from "./ReportsNew";
import ConductCheck from "./ConductCheck";
import PendingApprovalsView from "./components/PendingApprovalsView";
import RejectedSubmissionsView from "./components/RejectedSubmissionsView";
import UserRoleManager from "./components/UserRoleManager";
import RequirePermission from "./components/RequirePermission";
import Schedules from "./Schedules";
import Footer from "./components/Footer";
import PrintView from "./pages/PrintView";
import Analytics from "./pages/Analytics";
import { migrateUserNames } from "./utils/migrateUserNames";
import LanguageSwitcher from './components/LanguageSwitcher';

// Subscription pages
import LandingPage from "./pages/subscription/LandingPage";
import CompanySetup from "./pages/subscription/CompanySetup";
import TrialSuccess from "./pages/subscription/TrialSuccess";
import Checkout from "./pages/subscription/Checkout";
import CheckoutSuccess from "./pages/subscription/CheckoutSuccess";
import SubscriptionManagement from "./pages/subscription/SubscriptionManagement";
import UserManagement from "./pages/subscription/UserManagement";
import Features from "./pages/subscription/Features";
import Pricing from "./pages/subscription/Pricing";
import Contact from "./pages/subscription/Contact";
import TermsOfService from "./pages/subscription/TermsOfService";
import PrivacyPolicy from "./pages/subscription/PrivacyPolicy";
import RefundPolicy from "./pages/subscription/RefundPolicy";
import SubscriptionLocked from "./pages/subscription/SubscriptionLocked";
import DeleteAccount from "./pages/DeleteAccount";
import HowToGuide from "./pages/HowToGuide";
import ForgotPassword from "./ForgotPassword";
import ComplianceChecklistUK from "./pages/subscription/ComplianceChecklistUK";
import HealthSafetyInspection from "./pages/subscription/HealthSafetyInspection";
import DigitalInspectionChecklists from "./pages/subscription/DigitalInspectionChecklists";
import WhySpreadsheetsFail from "./pages/subscription/WhySpreadsheetsFail";
import PoliciesProcedures from "./pages/PoliciesProcedures";
import KnowledgeHub from "./pages/KnowledgeHub";
import FormAutomationPresetLibrary from "./pages/FormAutomationPresetLibrary";

export default function App() {
  const [user, setUser] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [loadingSplash, setLoadingSplash] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [hasShownLoginSplash, setHasShownLoginSplash] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const previousAuthUserRef = useRef(null);
  const isPrintPath = typeof window !== 'undefined' && window.location.pathname.startsWith('/print');

  useEffect(() => {
    // Normalize to light mode across the app
    document.documentElement.classList.remove('dark');
  }, []);

  useEffect(() => {
    let unsubscribeSnapshot = null;

    const unsub = onAuthStateChanged(auth, (u) => {
      if (!isLoggingOut) {
        // Show splash only on FIRST login (user going from null to authenticated)
        if (!previousAuthUserRef.current && u && !hasShownLoginSplash) {
          setLoadingSplash(true);
          setHasShownLoginSplash(true);
          setTimeout(() => setLoadingSplash(false), 3500);
        }
        previousAuthUserRef.current = u;
        setUser(u);

        // Clean up previous snapshot listener if it exists
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }

        if (u) {
          setLoading(true);
          // Use real-time listener for user profile to get company and role
          const userDocRef = doc(db, 'users', u.uid);
          unsubscribeSnapshot = onSnapshot(userDocRef, (userDoc) => {
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setCompanyId(userData.companyId || null);
              setUserRole(userData.role || null);
            } else {
              // User document doesn't exist, will be created during registration/setup
              setCompanyId(null);
              setUserRole(null);
            }
            setLoading(false);
          }, (error) => {
            console.error('Error listening to user profile:', error);
            setCompanyId(null);
            setUserRole(null);
            setLoading(false);
          });
        } else {
          setUserRole(null);
          setCompanyId(null);
          setCompanyData(null);
          setLoading(false);
        }
      }
    });

    const handleShowRegister = () => setShowRegister(true);
    const handleShowLogin = () => setShowRegister(false);
    window.addEventListener("show-register", handleShowRegister);
    window.addEventListener("show-login", handleShowLogin);

    // Global logout event → power-down splash
    const handleLogout = async () => {
      setIsLoggingOut(true);
      setTimeout(async () => {
        await signOut(auth);
        setIsLoggingOut(false);
        setUser(null);
      }, 3500);
    };
    window.addEventListener("logout", handleLogout);

    // Expose migration function to window
    if (typeof window !== 'undefined') {
      window.migrateUserNames = migrateUserNames;
    }

    return () => {
      unsub();
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      window.removeEventListener("show-register", handleShowRegister);
      window.removeEventListener("show-login", handleShowLogin);
      window.removeEventListener("logout", handleLogout);
    };
  }, [isLoggingOut, hasShownLoginSplash]);

  // Real-time listener for company subscription status
  useEffect(() => {
    if (!companyId) {
      setCompanyData(null);
      return;
    }

    const companyDocRef = doc(db, 'companies', companyId);
    const unsubscribeCompany = onSnapshot(companyDocRef, (companyDoc) => {
      if (companyDoc.exists()) {
        setCompanyData(companyDoc.data());
      } else {
        setCompanyData(null);
      }
    }, (error) => {
      console.error('Error listening to company doc:', error);
      setCompanyData(null);
    });

    return () => unsubscribeCompany();
  }, [companyId]);

  // Determine if subscription is locked (expired trial or explicitly locked)
  const isSubscriptionLocked = (() => {
    if (!companyData || !companyId) return false;
    const status = companyData.subscriptionStatus;
    // Explicitly locked or cancelled
    if (['locked', 'canceled', 'cancelled', 'past_due'].includes(status)) return true;
    // Trial expired
    if (status === 'trial' && companyData.trialEndsAt) {
      const endDate = typeof companyData.trialEndsAt === 'string'
        ? new Date(companyData.trialEndsAt)
        : companyData.trialEndsAt?.toDate
          ? companyData.trialEndsAt.toDate()
          : new Date(companyData.trialEndsAt);
      return endDate <= new Date();
    }
    return false;
  })();

  // Show loading spinner while checking authentication and user data
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  // Render
  if (!isPrintPath && loadingSplash) return <SplashScreen mode="on" onFinish={() => setLoadingSplash(false)} />;
  if (!isPrintPath && isLoggingOut) return <SplashScreen mode="off" onFinish={() => setIsLoggingOut(false)} />;

  if (!user)
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/compliance-checklist-software-uk" element={<ComplianceChecklistUK />} />
          <Route path="/health-and-safety-inspection-software" element={<HealthSafetyInspection />} />
          <Route path="/digital-inspection-checklists" element={<DigitalInspectionChecklists />} />
          <Route path="/why-spreadsheets-fail-inspections" element={<WhySpreadsheetsFail />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/guide" element={<HowToGuide />} />
          <Route path="/login" element={<Login onLogin={(u) => setUser(u)} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/register" element={<Register onRegister={(u) => setUser(u)} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    );

  // Authenticated: show Home first, then Dashboard/Reports
  return (
    <div className="min-h-screen bg-gray-100">
      <BrowserRouter>
        <Routes>
          {/* Public/Marketing Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/compliance-checklist-software-uk" element={<ComplianceChecklistUK />} />
          <Route path="/health-and-safety-inspection-software" element={<HealthSafetyInspection />} />
          <Route path="/digital-inspection-checklists" element={<DigitalInspectionChecklists />} />
          <Route path="/why-spreadsheets-fail-inspections" element={<WhySpreadsheetsFail />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/guide" element={<HowToGuide />} />

          {/* Authentication Routes */}
          <Route path="/login" element={!user ? <Login onLogin={(u) => setUser(u)} /> : <Navigate to={companyId ? "/home" : "/setup"} />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to={companyId ? "/home" : "/setup"} />} />
          <Route path="/register" element={!user ? <Register onRegister={(u) => setUser(u)} /> : <Navigate to="/setup" />} />

          {/* Company Setup Route */}
          <Route
            path="/setup"
            element={user && !companyId ? <CompanySetup /> : user && companyId ? <Navigate to="/home" /> : <Navigate to="/login" />}
          />

          {/* Checkout Route */}
          <Route
            path="/checkout"
            element={user ? <Checkout /> : <Navigate to="/login" />}
          />

          {/* Checkout Success Route (Stripe redirect) */}
          <Route
            path="/checkout-success"
            element={user ? <CheckoutSuccess /> : <Navigate to="/login" />}
          />

          {/* Trial Success Route */}
          <Route
            path="/subscription/trial-success"
            element={user ? <TrialSuccess /> : <Navigate to="/login" />}
          />

          {/* Protected App Routes — gated by subscription status */}
          <Route
            path="/home"
            element={
              user ? (
                !companyId ? <Navigate to="/setup" /> :
                isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> :
                <Home />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              user ? (
                !companyId ? <Navigate to="/setup" /> :
                isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> :
                <Dashboard />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/conduct"
            element={
              user && companyId
                ? (isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <ConductCheck />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/reports"
            element={
              user && companyId
                ? (isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <Reports />)
                : <Navigate to="/login" />
            }
          />
          <Route path="/print" element={<PrintView />} />
          <Route
            path="/analytics"
            element={
              user && companyId
                ? (isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <Analytics />)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/policies"
            element={
              user && companyId
                ? (isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="view_policies"><PoliciesProcedures /></RequirePermission>)
                : <Navigate to="/login" />
            }
          />
          <Route
            path="/knowledge-hub"
            element={
              user && companyId
                ? (isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="view_policies"><KnowledgeHub /></RequirePermission>)
                : <Navigate to="/login" />
            }
          />
          <Route path="/approvals" element={isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="approve_submissions"><PendingApprovalsView /></RequirePermission>} />
          <Route path="/rejected" element={isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="view_submissions"><RejectedSubmissionsView /></RequirePermission>} />
          <Route path="/manage-roles" element={isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="manage_users"><UserRoleManager /></RequirePermission>} />
          <Route path="/schedules" element={isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="set_schedules"><Schedules /></RequirePermission>} />
          <Route path="/preset-library" element={isSubscriptionLocked ? <SubscriptionLocked companyData={companyData} /> : <RequirePermission permission="create_forms"><FormAutomationPresetLibrary /></RequirePermission>} />

          {/* Admin Routes — subscription page always accessible so admins can reactivate */}
          <Route
            path="/subscription"
            element={
              user && userRole === 'admin' ? <SubscriptionManagement /> : <Navigate to="/home" />
            }
          />
          <Route
            path="/users"
            element={
              user && userRole === 'admin' ? <UserManagement /> : <Navigate to="/home" />
            }
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Footer />
      {/* Floating language switcher for authenticated pages */}
      <div className="fixed bottom-4 right-4 z-50">
        <LanguageSwitcher dropUp variant="dark" />
      </div>
    </div>
  );
}
